import { ArrowBack, PictureAsPdf } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material'
import { DeleteButton, EditButton, GreenText, LabelField, LockButton, LockedChip } from 'components'
import { useLayout } from 'context'
import { deleteOutboundTrip, generateBillOfLading, lock, unlock, updateOutboundTrip } from 'controllers/outboundTrips/index'
import { OutboundTripDetailDto, OutboundTripDto } from 'dtos/outboundTrips'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDateMoment } from 'utils'
import AddEditOutboundTripDialog from '../AddEditOutboundTripDialog'
import ViewSignedOutboundBillOfLadingLink from './ViewSignedOutboundBillOfLadingLink'

interface OutboundTripDetailCardProps {
  outboundTrip?: OutboundTripDto
  outboundTripGuid?: string
  setState: (outboundTripDetail: OutboundTripDetailDto) => void
}

export default function OutboundTripDetailCard({ outboundTrip, outboundTripGuid, setState }: OutboundTripDetailCardProps) {
  const { setIsLayoutLoading } = useLayout()
  const navigate = useNavigate()

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isGetting, setIsGetting] = useState<boolean>(false)
  const [isLocking, setIsLocking] = useState<boolean>(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  useEffect(() => {
    setIsLayoutLoading(isLocking || isDeleting)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLocking || isDeleting])

  return (
    <>
      {outboundTrip && (
        <AddEditOutboundTripDialog
          initialValues={outboundTrip}
          isLoading={isSaving}
          onClose={() => setDialogOpen(false)}
          onSave={values => {
            setIsSaving(true)
            updateOutboundTrip(values)
              .then(res => {
                enqueueSnackbar('Outbound Trip Updated Successfully!', { variant: 'success' })
                setState({ outboundTrip: res.value })
                setDialogOpen(false)
              })
              .finally(() => setIsSaving(false))
          }}
          open={dialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto' container spacing={1}>
                <Grid item>
                  <Typography fontWeight='bold' variant='h5'>
                    Outbound Trip #{outboundTrip?.outboundTripNumber}
                  </Typography>
                </Grid>

                <Grid item>
                  <LockedChip isLocked={outboundTrip?.isLocked} />
                </Grid>
              </Grid>

              <Grid item xs={12} sm='auto' container>
                <Grid item>
                  <Button color='primary' onClick={() => navigate(-1)} startIcon={<ArrowBack />} variant='text'>
                    BACK
                  </Button>
                </Grid>

                <Grid item sx={{ alignSelf: 'stretch' }}>
                  <Divider orientation='vertical' />
                </Grid>

                <Grid item>
                  <LoadingButton
                    disabled={!outboundTrip?.outboundTripGuid || !outboundTrip?.isLocked}
                    endIcon={<PictureAsPdf />}
                    loading={isGetting}
                    onClick={() => {
                      setIsGetting(true)
                      generateBillOfLading(outboundTripGuid!).finally(() => setIsGetting(false))
                    }}
                    variant='text'
                  >
                    GENERATE BILL OF LADING
                  </LoadingButton>
                </Grid>

                <Grid item>
                  <LockButton
                    isLocked={outboundTrip?.isLocked}
                    onLock={() => {
                      setIsLocking(true)
                      lock(outboundTripGuid!)
                        .then(res => {
                          enqueueSnackbar('Outbound Trip Locked Successfully!', { variant: 'success' })
                          setState({ outboundTrip: res.value })
                        })
                        .finally(() => setIsLocking(false))
                    }}
                    onUnlock={() => {
                      setIsLocking(true)
                      unlock(outboundTripGuid!)
                        .then(res => {
                          enqueueSnackbar('Outbound Trip Unlocked Successfully!', { variant: 'success' })
                          setState({ outboundTrip: res.value })
                        })
                        .finally(() => setIsLocking(false))
                    }}
                  />
                </Grid>

                <Grid item>
                  <DeleteButton
                    disabled={!outboundTrip?.outboundTripGuid || outboundTrip?.isLocked}
                    name='Outbound Trip'
                    onDelete={() => {
                      setIsDeleting(true)
                      deleteOutboundTrip(outboundTripGuid!)
                        .then(() => {
                          enqueueSnackbar('Outbound Trip Deleted Successfully!', { variant: 'success' })
                          navigate('/outbound-trips', { replace: true })
                        })
                        .finally(() => setIsDeleting(false))
                    }}
                  />
                </Grid>

                <Grid item>
                  <EditButton disabled={outboundTrip?.isLocked} onEdit={() => setDialogOpen(true)} />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item sm={6} md={3}>
              <LabelField label='Outbound Trip Date' value={outboundTrip?.outboundTripDate ? formatDateMoment(outboundTrip.outboundTripDate) : undefined} />
            </Grid>

            <Grid item sm={6} md={3}>
              <LabelField label='Outbound Trip ID' value={outboundTrip?.id} />
            </Grid>

            <Grid item sm={6} md={3}>
              <LabelField label='Outbound Transporter Company' value={outboundTrip?.outboundTransporterCompany.name} />
            </Grid>

            <Grid item sm={6} md={3}>
              <LabelField label='DOT #' value={outboundTrip?.outboundTransporterCompany.dotNumber} />
            </Grid>

            {outboundTrip?.outboundTripGuid && outboundTrip?.hasSignedBOL && (
              <Grid item xs={12}>
                <ViewSignedOutboundBillOfLadingLink outbobundTripGuid={outboundTrip?.outboundTripGuid} />
              </Grid>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Outbound Trip Locked Chip:**

Displays when a Outbound Trip is marked as locked.

**Back Button:**

Clicking this button navigates the user to the previous screen.

**Generate Outbound Bill Of Lading Button:**

Clicking this button downloads a PDF to the user’s device containing:

   - The Outbound Bill of Lading
   - The Warranty Certificate for each vehicle in the Outbound Trip.

This button is enabled only when the Outbound Trip is locked.

**Lock/Unlock Outbound Trip Button:**

Lock Outbound Trip Button: Clicking this button locks the Outbound Trip and changes the button text to "Unlock Outbound Trip."

Unlock Outbound Trip Button: Clicking this button unlocks the Outbound Trip.

Outbound Trips cannot be unlocked once a Signed BOL has been successfully associated with the Outbound Trip.

**Delete Outbound Trip Button:**

Clicking this button deletes the Outbound Trip.

Outbound Trips can only be deleted if the trip is not locked.

**Edit Button:**

Clicking this button opens the Edit Outbound Trip dialog.

This button is enabled only if the Outbound Trip is not locked.

**Outbound Trip Date Label:**

Displays the scheduled date of the Outbound Trip.

**Outbound Trip ID Label:**

Displays the system-generated ID assigned when an Outbound Trip is created.

The first ID begins at “OT100000” and auto-increments by 1 for each new Outbound Trip.

This ID is displayed as a barcode at the top of the Outbound Bill of Lading.

It is used by the OCR feature to associate a Signed Outbound Bill of Lading (Signed BOL) with the corresponding Outbound Trip.

**Outbound Transporter Company Label:**

Displays the company name for the Outbound Transporter Company.

**DOT # Label:**

Displays the DOT Number for the Outbound Trip.

**View Signed Outbound Bill Of Lading Link:**

This link appears after a Signed BOL has been OCR’d and associated with the Outbound Trip.

Clicking the link opens the Signed BOL in a new browser tab.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
